import { useRef, useState, type ChangeEvent } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { CATEGORIAS } from '@/pages/assistente-evento/constants';

interface Props {
  eventoId: number;
  nomeInicial: string;
  descricaoInicial: string;
  categoriaInicial: string;
  capaInicial: string | null;
  aoFechar: () => void;
  aoSalvo: () => void;
}

const EXTENSOES_OK = ['image/jpeg', 'image/png', 'image/webp'];
const TAMANHO_MAX = 5 * 1024 * 1024;

const inputCls =
  'mt-1 w-full rounded-md border border-secondary-300 bg-white px-3 py-2 text-[15px] text-foreground-950 outline-none transition-colors focus:border-primary-500';

export default function OQueEditor({
  eventoId,
  nomeInicial,
  descricaoInicial,
  categoriaInicial,
  capaInicial,
  aoFechar,
  aoSalvo,
}: Props) {
  const [nome, setNome] = useState(nomeInicial);
  const [descricao, setDescricao] = useState(descricaoInicial);
  const [categoria, setCategoria] = useState(categoriaInicial);
  const [capa, setCapa] = useState(capaInicial);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [removerCapa, setRemoverCapa] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  function escolher(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    if (!f) return;
    if (!EXTENSOES_OK.includes(f.type) || f.size > TAMANHO_MAX) {
      setErro('Escolha uma imagem jpg, png ou webp de até 5 MB.');
      return;
    }
    setErro(null);
    setArquivo(f);
    setRemoverCapa(false);
    setCapa(URL.createObjectURL(f));
  }

  async function salvar() {
    if (!nome.trim()) {
      setErro('Digite o nome do evento.');
      return;
    }
    setSalvando(true);
    setErro(null);
    try {
      let novaCapa: string | null | undefined;
      if (arquivo) {
        const ext = arquivo.name.split('.').pop()?.toLowerCase() || 'jpg';
        const caminho = `${eventoId}/arte-${Date.now()}.${ext}`;
        const { error } = await supabase.storage.from('capas').upload(caminho, arquivo);
        if (error) throw error;
        const { data } = supabase.storage.from('capas').getPublicUrl(caminho);
        novaCapa = data.publicUrl;
      } else if (removerCapa) {
        novaCapa = null;
      }

      const dados: Record<string, unknown> = {
        nome: nome.trim(),
        descricao: descricao.trim() || null,
        categoria: categoria || 'outro',
      };
      if (novaCapa !== undefined) {
        dados.imagem_desktop_url = novaCapa;
        dados.imagem_mobile_url = novaCapa;
      }

      const { error } = await supabase.from('eventos').update(dados).eq('id', eventoId);
      if (error) throw error;
      aoSalvo();
      aoFechar();
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="space-y-5">
      {erro && (
        <p className="rounded-md border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700">
          {erro}
        </p>
      )}

      <label className="block">
        <span className="text-sm font-medium text-foreground-800">Nome do evento</span>
        <input
          type="text"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          className={inputCls}
          placeholder="Luau da Juventude"
        />
      </label>

      <label className="block">
        <span className="text-sm font-medium text-foreground-800">Categoria</span>
        <select
          value={categoria}
          onChange={(e) => setCategoria(e.target.value)}
          className={`${inputCls} cursor-pointer`}
        >
          <option value="">Escolha</option>
          {CATEGORIAS.map((c) => (
            <option key={c.valor} value={c.valor}>
              {c.rotulo}
            </option>
          ))}
        </select>
      </label>

      <label className="block">
        <span className="text-sm font-medium text-foreground-800">Descrição</span>
        <textarea
          value={descricao}
          onChange={(e) => setDescricao(e.target.value.slice(0, 2000))}
          rows={5}
          className={`${inputCls} resize-none`}
          placeholder="Um fim de semana de oração, convivência e formação para jovens…"
        />
      </label>

      <div>
        <span className="text-sm font-medium text-foreground-800">Capa</span>
        <p className="mt-0.5 text-xs text-secondary-500">
          Aparece no convite e na lista. jpg, png ou webp até 5 MB.
        </p>
        <input
          ref={inputRef}
          type="file"
          accept=".jpg,.jpeg,.png,.webp"
          className="sr-only"
          onChange={escolher}
        />
        {capa ? (
          <div className="mt-2 overflow-hidden rounded-lg border border-secondary-200">
            <div className="relative">
              <img src={capa} alt="Capa do evento" className="aspect-video w-full object-top" />
              <button
                type="button"
                onClick={() => {
                  setArquivo(null);
                  setRemoverCapa(true);
                  setCapa(null);
                }}
                aria-label="Remover capa"
                className="absolute right-2 top-2 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-black/60 text-white transition-colors hover:bg-black/80"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="mt-2 flex aspect-video w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-secondary-300 bg-white text-center transition-colors hover:border-primary-400 hover:bg-background-50"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 text-primary-600">
              <i className="ri-image-add-line text-xl" />
            </span>
            <span className="mt-2 text-sm font-medium text-foreground-800">Escolher imagem</span>
          </button>
        )}
      </div>

      <div className="flex gap-2 pt-1">
        <button
          type="button"
          onClick={salvar}
          disabled={salvando}
          className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {salvando ? 'Salvando…' : 'Salvar'}
        </button>
        <button
          type="button"
          onClick={aoFechar}
          disabled={salvando}
          className="cursor-pointer whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium text-secondary-600 transition-colors hover:bg-background-100 disabled:opacity-50"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}